import { AddCircleIcon } from "icons/payments/AddCircleIcon";
import { CloseCircleIcon } from "icons/payments/CloseCircleIcon";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import DateRangePicker from "react-bootstrap-daterangepicker";

interface DateState {
  start: any;
  end: any;
}

interface DatePickerFilterProps {
  dateState: DateState;
  timeRanges: Record<string, [Date, Date]>;
  handleCallback: (start: any, end: any) => void;
  handleShow: () => void;
  handleHide: () => void;
  openCal: boolean;
  setOpenCal: React.Dispatch<React.SetStateAction<boolean>>;
  setDateState: React.Dispatch<React.SetStateAction<DateState>>;
  label?: string | null;
  onOpen?: () => void;
}

const DatePickerFilter: React.FC<DatePickerFilterProps> = ({
  dateState,
  timeRanges,
  handleCallback,
  handleShow,
  handleHide,
  openCal,
  setOpenCal,
  setDateState,
  label,
  onOpen = () => {},
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [datePickerKey, setDatePickerKey] = useState<number>(0);
  // Determine if custom range is selected
  const isCustom =
    label === "Custom" || (label && label.toLowerCase().includes("custom"));

  const customHandleShow = () => {
    setTimeout(() => {
      const ranges = document.querySelectorAll(".daterangepicker .ranges li");
      ranges.forEach((li) => {
        if (li.querySelector(".date-range-blue-tick")) {
          li.querySelector(".date-range-blue-tick")?.remove();
        }
        if (li.classList.contains("active")) {
          li.classList.add("flex", "items-center", "justfy-between");
          const span = document.createElement("span");
          span.innerHTML = `<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.125 7.5L6.25 10.625L12.5 4.375" stroke="#1678F2" strokeWidth="2.16667" strokeLinecap="round" strokeLinejoin="round"/></svg>`;
          span.classList.add("date-range-blue-tick");
          li.appendChild(span);
        }
      });
      updatePickerPosition();
    }, 0);
    if (handleShow) handleShow();
  };

  const updatePickerPosition = useCallback(() => {
    const picker = document.querySelector(
      ".daterangepicker"
    ) as HTMLElement | null;
    const trigger = triggerRef.current;

    if (!picker || !trigger) return;

    // Coordinates stay in real screen px. Unlike the other portalled filters
    // this element positions itself, so scaling it here would drag its own
    // top/left along; index.css scales `.daterangepicker > *` instead, leaving
    // this box unscaled and letting its auto width shrink-wrap the result.
    const triggerRect = trigger.getBoundingClientRect();
    const viewportPadding = 12;
    const pickerWidth = Math.min(
      Math.max(picker.offsetWidth, 280),
      window.innerWidth - viewportPadding * 2
    );
    const preferredLeft = triggerRect.left;
    const maxLeft = window.innerWidth - pickerWidth - viewportPadding;

    picker.style.setProperty("position", "fixed", "important");
    picker.style.setProperty("top", `${triggerRect.bottom + 5}px`, "important");
    picker.style.setProperty(
      "left",
      `${Math.max(viewportPadding, Math.min(preferredLeft, maxLeft))}px`,
      "important"
    );
    picker.style.setProperty("right", "auto", "important");
    picker.style.setProperty("width", `${pickerWidth}px`, "important");
    picker.style.setProperty("max-width", `${pickerWidth}px`, "important");
    picker.style.setProperty("z-index", "9999", "important");
  }, []);

  // The library's own `onShow` never reaches us here, so the picker kept the
  // position bootstrap-daterangepicker computed for it. That was harmless while
  // the page rendered 1:1, but the shell now scales and the library reads the
  // trigger in layout px, leaving the panel ~12% of its offset too far right.
  // Repositioning from the trigger's own handler is the one path guaranteed to
  // fire; the retries cover the frames before the picker is in the DOM.
  const schedulePickerReposition = useCallback(() => {
    [0, 30, 120].forEach((delay) => setTimeout(updatePickerPosition, delay));
  }, [updatePickerPosition]);

  useEffect(() => {
    if (!openCal) return;

    const handleResize = () => {
      const picker = document.querySelector(
        ".daterangepicker"
      ) as HTMLElement | null;
      if (!picker || picker.style.display === "none") return;
      updatePickerPosition();
    };

    const handleScroll = () => {
      const picker = document.querySelector(
        ".daterangepicker"
      ) as HTMLElement | null;
      if (!picker || picker.style.display === "none") return;
      const cancelButton = picker.querySelector(
        ".cancelBtn, .cancelDateRangeButton"
      ) as HTMLButtonElement | null;
      cancelButton?.click();
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);
    updatePickerPosition();

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [openCal, updatePickerPosition]);

  return (
    <div className="relative w-full">
      <DateRangePicker
        key={datePickerKey}
        initialSettings={{
          startDate: dateState?.start || moment().startOf("month").toDate(),
          endDate: dateState?.end || moment().toDate(),
          ranges: timeRanges,
          locale: { customRangeLabel: "Custom" },
          opens: "left",
          applyButtonClasses: "applyDateRangeButton",
          cancelButtonClasses: "cancelDateRangeButton",
        }}
        onShowCalendar={() => {
          setTimeout(() => {
            const buttons = document.querySelector(
              ".daterangepicker .drp-buttons"
            ) as HTMLElement | null;
            if (!buttons) return;
            buttons.style.setProperty("display", "block", "important");
            updatePickerPosition();
          }, 0);
        }}
        onHideCalendar={() => {
          setTimeout(() => {
            const buttons = document.querySelector(
              ".daterangepicker .drp-buttons"
            ) as HTMLElement | null;
            if (!buttons) return;
            buttons.style.setProperty("display", "none", "important");
          }, 0);
        }}
        onCallback={handleCallback}
        onShow={customHandleShow}
        onHide={handleHide}
      >
        <div
          id="reportrange"
          ref={triggerRef}
          role="button"
          tabIndex={0}
          aria-expanded={openCal}
          aria-haspopup="dialog"
          aria-label="Date filter"
          className="rounded-[20px] w-full min-w-0 hover:bg-bgHover border-[1px] border-strokePrimary bg-bgPrimary h-[34px] flex items-center gap-[6px] pl-[9px] pr-[15px] cursor-pointer"
          onMouseDown={() => {
            onOpen();
            schedulePickerReposition();
          }}
          onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault();
            onOpen();
            triggerRef.current?.click();
            schedulePickerReposition();
          }}
        >
          <div
            className="cursor-pointer shrink-0"
            onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
              if (openCal) {
                e.preventDefault();
                e.stopPropagation();
                setOpenCal(false);
                setDateState({ start: "", end: "" });
                setDatePickerKey((k) => k + 1);
              }
            }}
            onClick={(e) => {
              if (openCal) {
                e.stopPropagation();
                e.preventDefault();
              }
            }}
          >
            {openCal ? <CloseCircleIcon /> : <AddCircleIcon />}
          </div>
          <div className="text-txtParagraph leading-normal font-medium text-xs whitespace-nowrap shrink-0">
            Date
          </div>
          {label && (
            <div className="h-[16px] w-[1px] bg-txtParagraph shrink-0" />
          )}
          {label && (
            <span
              title={label}
              className="text-txtPrimary md:text-sm text-xs truncate font-bold min-w-0"
            >
              {label}
            </span>
          )}
          {isCustom && (
            <button
              type="button"
              className="ml-2 px-3 py-1 rounded bg-blue text-white text-xs font-semibold hover:bg-blue-700 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (handleCallback)
                  handleCallback(dateState.start, dateState.end);
              }}
            >
              Apply
            </button>
          )}
        </div>
      </DateRangePicker>
    </div>
  );
};

export default DatePickerFilter;
